import { useState, useEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ThemeProvider } from './contexts/ThemeContext';
import { Sidebar } from './components/ui/Sidebar';
import { ModernChatBox } from './components/ModernChatBox';
import { ModernFileManager } from './components/ModernFileManager';
import { ModernFileViewer } from './components/ModernFileViewer';
import { ModernAdminPanel } from './components/ModernAdminPanel';
import { LoginPage } from './components/auth/LoginPage';
import { msalInstance } from './msalConfig';
import clsx from 'clsx';

function AppContent() {
  const [activeTab, setActiveTab] = useState('chat');
  const [user, setUser] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [mainChunks, setMainChunks] = useState([]);
  const [showContext, setShowContext] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [useHistory, setUseHistory] = useState(true);
  const [hasResetChatHistory, setHasResetChatHistory] = useState(false);
  const [chatHistory, setChatHistory] = useState([
  {
    type: 'bot',
    text: '👋 Hallo, ik ben Luna! Waarmee kan ik je helpen? Typ je vraag hieronder in de balk en ik help je graag verder.',
    contexts: []
  }
]);


  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleFileSelect = (file) => {
    setSelectedFile(file);
    if (isMobile) {
      setShowFileViewer(true);
    }
  };

  const handleBackToFileList = () => {
    setShowFileViewer(false);
  };

  const handleLoginSuccess = (userData) => {
    setUser(userData);
  };

  const logout = () => {
    msalInstance.logoutRedirect({
      postLogoutRedirectUri: '/auth/login'
    });
    setUser(null);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'chat':
        return (
          <div className="h-full">
            <ModernChatBox
              user={user}
              chat={chatHistory}
              setChat={setChatHistory}
              onMainChunksUpdate={setMainChunks}
              showContext={showContext}
              setShowContext={setShowContext}
              useHistory={useHistory}
              onHistoryChange={setUseHistory}
              hasResetHistory={hasResetChatHistory}
              setHasResetHistory={setHasResetChatHistory}
            />
          </div>
        );
      case 'files':
        return (
          <div className="h-full flex flex-col lg:flex-row">
            {/* Desktop: beide panels naast elkaar */}
            {/* Mobiel: toggle tussen file manager en file viewer */}
            {!isMobile ? (
              // Desktop layout
              <>
                <div className="w-1/2 h-full">
                  <ModernFileManager onSelect={handleFileSelect} user={user} />
                </div>
                <div className="w-1/2 h-full">
                  <ModernFileViewer file={selectedFile} user={user} />
                </div>
              </>
            ) : (
              // Mobile layout
              <>
                {!showFileViewer ? (
                  <div className="w-full h-full">
                    <ModernFileManager onSelect={handleFileSelect} user={user} />
                  </div>
                ) : (
                  <div className="w-full h-full">
                    <ModernFileViewer 
                      file={selectedFile} 
                      user={user}
                      onBack={handleBackToFileList}
                      isMobile={isMobile}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        );
      case 'admin':
        return (
          <div className="h-full">
            <ModernAdminPanel 
              useHistory={useHistory}
              onHistoryChange={setUseHistory}
            />
          </div>
        );
      default:
        return null;
    }
  };

  if (!user) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="h-screen bg-gray-50 dark:bg-dark-950 flex overflow-hidden">
      {/* Sidebar */}
      <Sidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        user={user}
        onLogout={logout}
        isMobile={isMobile}
      />

      {/* Main content */}
      <div className={clsx(
        "flex-1 flex flex-col min-w-0",
        !isMobile && "lg:ml-64"
      )}>
        {/* Mobile header spacer - only show for non-chat views */}
        {isMobile && activeTab !== 'chat' && <div className="h-16" />}
        
        {/* Content area */}
        <main className={clsx(
          "flex-1 overflow-hidden",
          isMobile && activeTab === 'chat' && "h-screen"
        )}>
          {renderContent()}
        </main>
      </div>

      {/* Toast container */}
      <ToastContainer
        position={isMobile ? "top-right" : "bottom-right"}
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        className="z-50"
      />
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}
